import React from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { GridColDef } from '@mui/x-data-grid';
import { FDataGrid, FGridMoneyCell } from '../grid';
import {
  fetchOfficialPpmReport,
  fetchOfficialPpmRows,
  fetchOfficialPpmSummary,
} from './procurement.apis';
import { exportPpmRowsToExcel } from './ppmReportExport';
type Props = {
  embedded?: boolean;
  planningcycleid?: number;
  ppmid?: number | null;
};
type State = {
  ppmId: number | '';
  report: any | null;
  rows: any[];
  summary: any[];
  loading: boolean;
  error: string;
  success: string;
};
export default class PpmReportWorkbench extends React.Component<Props, State> {
  state: State = {
    ppmId: this.props.ppmid ?? '',
    report: null,
    rows: [],
    summary: [],
    loading: false,
    error: '',
    success: '',
  };
  componentDidMount() {
    if (this.props.ppmid) void this.load();
  }
  componentDidUpdate(p: Props) {
    if (p.ppmid !== this.props.ppmid && this.props.ppmid)
      this.setState({ ppmId: this.props.ppmid }, () => void this.load());
  }
  private columns: GridColDef[] = [
    { field: 'psc', headerName: 'Section', width: 140 },
    { field: 'acc', headerName: 'Account', width: 120 },
    { field: 'acn', headerName: 'Account Name', flex: 1, minWidth: 250 },
    {
      field: 'bga',
      headerName: 'Budget',
      width: 140,
      renderCell: FGridMoneyCell,
    },
    {
      field: 'ara',
      headerName: 'Arrears',
      width: 120,
      renderCell: FGridMoneyCell,
    },
    {
      field: 'avc',
      headerName: 'Available Credit',
      width: 150,
      renderCell: FGridMoneyCell,
    },
    { field: 'atx', headerName: 'Activity', flex: 1, minWidth: 300 },
    { field: 'pnt', headerName: 'Nature', width: 180 },
    { field: 'pmd', headerName: 'Method', width: 150 },
    { field: 'rvt', headerName: 'Review', width: 120 },
    { field: 'tpd', headerName: 'Publication', width: 140 },
    { field: 'bsd', headerName: 'Bid Deadline', width: 140 },
    { field: 'psd', headerName: 'Start', width: 130 },
    { field: 'eed', headerName: 'End', width: 130 },
    { field: 'lot', headerName: 'Lot', width: 100 },
    { field: 'san', headerName: 'Superactivity', width: 180 },
  ];
  private load = async () => {
    if (!this.state.ppmId) {
      this.setState({ error: 'Select a PPM first.' });
      return;
    }
    this.setState({ loading: true, error: '', success: '' });
    try {
      const pci = Number(this.props.planningcycleid ?? 0);
      const [report, rows, summary] = await Promise.all([
        fetchOfficialPpmReport(pci, Number(this.state.ppmId)),
        fetchOfficialPpmRows(pci, Number(this.state.ppmId)),
        fetchOfficialPpmSummary(pci, Number(this.state.ppmId)),
      ]);
      this.setState({ report, rows, summary, success: 'PPM report loaded.' });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };
  render() {
    const s = this.state;
    return (
      <Box>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Official PPM Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Read-only official procurement plan derived from the selected
                PPM.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              {!this.props.embedded && (
                <TextField
                  size="small"
                  label="PPM ID"
                  type="number"
                  value={s.ppmId}
                  onChange={(e) =>
                    this.setState({
                      ppmId:
                        e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                />
              )}
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={this.load}
                disabled={s.loading}
              >
                Load
              </Button>
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={() =>
                  exportPpmRowsToExcel(s.rows, `ppm_${s.ppmId}.xlsx`)
                }
                disabled={!s.rows.length}
              >
                Excel
              </Button>
            </Stack>
          </Stack>
          {s.error && <Alert severity="error">{s.error}</Alert>}
          <FDataGrid
            title="PPM Report Rows"
            rows={s.rows}
            columns={this.columns}
            loading={s.loading}
            height={650}
            getRowId={(r: any) => r.rid || r.id}
            sx={{ '& .MuiDataGrid-cell': { bgcolor: 'action.hover' } }}
          />
        </Stack>
      </Box>
    );
  }
}
